import psaLogo from '@/assets/images/psa-logo.png'
import bgsLogo from '@/assets/images/bgs-logo.png'
import cgcLogo from '@/assets/images/cgc-logo.jpg'

const GRADE_LOGO_MAP = {
  PSA: psaLogo,
  BGS: bgsLogo,
  CGC: cgcLogo,
}

const STORAGE_KEY = 'opcardx_card_list'

/**
 * 根据评级机构名称返回对应 logo 图片
 * @param {string} gradeName - 评级机构名，如 'PSA'、'BGS'
 * @returns {string|null} 图片路径，无匹配时返回 null
 */
export function getGradeLogo(gradeName) {
  if (!gradeName) return null
  return GRADE_LOGO_MAP[gradeName.toUpperCase()] || null
}

/**
 * 稀有度简写映射
 */
const RARITY_MAP = {
  'None': 'N/A',
  'Super Rare': 'SR',
  'Rare': 'R',
  'Leader': 'L',
  'Treasure Rare': 'TR',
  'Secret Rare': 'SEC',
  'DON!!': 'DON',
  'Uncommon': 'UC',
  'Common': 'C',
  'Promo': 'P'
}

/**
 * 将接口返回的稀有度转换为简写
 * @param {string} rarity
 * @returns {string}
 */
export function formatRarity (rarity) {
  if (!rarity) return '--'
  return RARITY_MAP[rarity] || rarity
}

/**
 * 将卡牌数组存入 localStorage
 * @param {Array} list - 卡牌数组，每项需包含 id 字段
 */
export function saveCardList (list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch (e) {
    console.error('saveCardList error:', e)
  }
}

/**
 * 从 localStorage 获取相邻卡牌 id
 * @param {string} direction - 'prev' | 'next'
 * @param {number|string} id - 当前卡牌 id
 * @param {string} key - id 字段名，默认 'id'
 * @returns {number|string|null} 相邻卡牌的 id，边界时返回 null 并 toast 提示
 */
export function getAdjacentCardId (direction, currentIndex, key = 'cardId') {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const list = JSON.parse(raw)
    const index = Number(currentIndex)

    if (isNaN(index) || index < 0 || index >= list.length) return null

    const currentVal = String(list[index][key])

    if (direction === 'prev') {
      let i = index - 1
      while (i >= 0 && String(list[i][key]) === currentVal) i--
      if (i < 0) return { id: null, index: null, isFirst: true }
      return { id: list[i][key], index: i, isFirst: false }
    }

    if (direction === 'next') {
      let i = index + 1
      while (i < list.length && String(list[i][key]) === currentVal) i++
      if (i >= list.length) return { id: null, index: null, isLast: true }
      return { id: list[i][key], index: i, isLast: false }
    }

    return null
  } catch (e) {
    console.error('getAdjacentCardId error:', e)
    return null
  }
}
/**
 * 将 USD 价格按当前币种汇率换算并格式化
 * @param {number|string} usdPrice - 原始 USD 价格
 * @param {object} options
 * @param {number} options.rate - 当前汇率（相对 USD）
 * @param {string} options.symbol - 货币符号，如 '$'、'HK$'、'JPY'
 * @param {string} options.currency - 货币代码，如 'USD'、'HKD'、'JPY'
 * @returns {string} 格式化后的价格字符串，如 "$1.80"、"HK$14.04"、"JPY 270"
 */
export function formatCurrencyPrice(usdPrice, { rate = 1, symbol = '$', currency = 'USD' } = {}) {
  if (usdPrice === undefined || usdPrice === null || usdPrice === '' || usdPrice === '--') return '--'
  const num = Number(usdPrice)
  if (isNaN(num)) return '--'
  const converted = num * rate

  // 如果小数部分为 0 就不显示小数
  const decimals = converted % 1 === 0 ? 0 : 2
  const formatted = converted.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return `${symbol}${formatted}`
}

/**
 * 更新 opcardx_scan_results 中指定 id 卡牌的 collectedQuantity
 * @param {number|string} id - 卡牌 id
 * @param {number} quantity - 新数量
 */
export function updateScanResultQuantity (id, quantity) {
  try {
    const raw = localStorage.getItem('opcardx_scan_results')
    if (!raw) return
    const list = JSON.parse(raw)
    const card = list.find(item => String(item.id) === String(id))
    if (card) {
      card.collectedQuantity = quantity
      localStorage.setItem('opcardx_scan_results', JSON.stringify(list))
    }
  } catch (e) {
    console.error('updateScanResultQuantity error:', e)
  }
}